<template>
  <div class="ms-resize-wrapper" :style="styleWidthComputed">
    <div
      class="resize resize-left"
      v-if="isResizeLeft && (explantToggle || resizeWhenClose)"
      @mousedown.prevent="
        e => {
          resizeOn(e, -1);
        }
      "
      @mouseup.prevent="resizeOff"
    ></div>
    <button
      v-if="toggleExplandButton"
      class="icon24"
      :class="{
        expand: explantToggle,
        'toggle toggle-left': toggleExplandButton == 'left',
        'toggle toggle-right': toggleExplandButton == 'right',
      }"
      @click="switchToggle"
      v-tooltip="explantToggle ? toggleButtonTooltipLeft : toggleButtonTooltipRight"
    ></button>
    <slot :explantToggle="explantToggle" :width="width"> </slot>
    <div
      class="resize resize-right"
      v-if="isResizeRight && (explantToggle || resizeWhenClose)"
      @mousedown.prevent="
        e => {
          resizeOn(e, 1);
        }
      "
      @mouseup.prevent="resizeOff"
    ></div>
  </div>
</template>
<script>
import { defineComponent, onMounted, reactive, ref, getCurrentInstance, computed, watch } from 'vue';

export default {
  name: 'msResizeWrapper',
  props: {
    // độ rộng nhỏ nhất có thể kéo
    minWidth: {
      default: 0,
      type: Number,
    },
    // độ rộng tối đa có thể kéo
    maxWidth: {
      default: 500,
      type: Number,
    },
    // có thể resize bên trái
    isResizeLeft: {
      default: false,
      type: Boolean,
    },
    // có thể resize bên phải
    isResizeRight: {
      default: false,
      type: Boolean,
    },
    // có nút mở rộng giá trị (left, right)
    toggleExplandButton: {
      default: false,
      type: String,
    },
    // trạng thái mặc định của toggle
    defaultexplantToggle: {
      default: true,
      type: Boolean,
    },
    // giá trị mặc định của with
    defaultWidth: {
      default: 0,
      type: Number,
    },
    // giá trị width khi đóng = min-width ? nếu false thì dóng hẳn
    closeCollapseMinWidth: {
      default: false,
      type: Boolean,
    },
    // khi đóng thì có kéo được nữa không
    resizeWhenClose: {
      default: false,
      type: Boolean,
    },
    // tooltip toggle button
    toggleButtonTooltipLeft: {
      default: '',
      type: String
    },
    // tooltip toggle button
    toggleButtonTooltipRight: {
      default: '',
      type: String
    },
  },
  setup(props) {
    const { proxy } = getCurrentInstance();

    const width = ref(props.defaultWidth || props.minWidth);

    // vị trí đầu tiên khi bấm kéo
    const startPositionResize = ref(0);
    // vị trí con trỏ chuột dang kéo
    const endPositionResize = ref(0);
    // có đang trong trạng thái kéo
    const isResizeActive = ref(false);
    // nếu resize bên trái thì signResie = -1, bên phải = 1
    const signResize = ref(1);
    // lưu trạng thái explan của toggle
    const explantToggle = computed(() => {
      let minWidthValue = props.closeCollapseMinWidth ? props.minWidth : 0;
      return width.value > minWidthValue;
    });

    const styleWidthComputed = computed(() => {
      let widthValue = props.closeCollapseMinWidth || explantToggle ? width.value : 0;
      return {
        width: `${widthValue}px`,
        'min-width': `${widthValue}px`,
        'max-width': `${props.maxWidth}px`,
      };
    });

    // theo dõi khi thay đổi vị trí kéo => thay đổi độ rộng
    watch(endPositionResize, newVal => {
      let newWidth = proxy.width + (endPositionResize.value - startPositionResize.value) * signResize.value;
      if (newWidth < props.minWidth) {
        width.value = props.minWidth;
      } else if (newWidth > props.maxWidth) {
        width.value = props.maxWidth;
      } else {
        startPositionResize.value = endPositionResize.value;
        width.value = newWidth;
      }
    });

    /**
     * Sự kiện đang kéo chuột
     * Created by NNKiem 24/04/2023
     */
    const mouseMove = e => {
      endPositionResize.value = e.clientX;
      document.addEventListener('mouseup', resizeOff);
      if (!isResizeActive.value) {
        isResizeActive.value = true;
      }
    };

    /**
     * Sự kiện dừng kéo
     * Created by NNKiem 24/04/2023
     */
    const resizeOff = e => {
      isResizeActive.value = false;
      document.removeEventListener('mousemove', mouseMove);
      document.removeEventListener('mouseup', resizeOff);
    };

    /**
     * Sử lý sự kiện bắt đầu kéo
     * param sign để đánh dấu đang resize bên trái hay bên phải (1 phải -1 trái)
     * Created by NNKiem 24/04/2023
     */
    const resizeOn = (e, sign = 1) => {
      signResize.value = sign;
      startPositionResize.value = e.clientX;
      document.addEventListener('mousemove', mouseMove);
      document.addEventListener('mouseup', resizeOff);
    };

    /**
     * Sự kiện bấm Toggle Explant bên trái
     * Created by NNKiem 24/04/2023
     */
    const switchToggle = function () {
      if (props.closeCollapseMinWidth && proxy.explantToggle) {
        width.value = props.minWidth;
      } else if (!props.closeCollapseMinWidth && proxy.explantToggle) {
        width.value = 0;
      } else if (!proxy.explantToggle) {
        width.value = props.defaultWidth;
      }
    };

    return {
      width,
      resizeOn,
      resizeOff,
      mouseMove,
      explantToggle,
      switchToggle,
      styleWidthComputed,
    };
  },
};
</script>
<style lang="scss" scoped>
.ms-resize-wrapper {
  position: relative;
  height: 100%;

  &.narrow-down {
    width: 0px !important;
    min-width: 0px !important;
    max-width: 0px !important;
  }

  .resize {
    position: absolute;
    height: 100%;
    width: 4px;
    cursor: col-resize;
    background: $secondary;
    top: 0;
    z-index: 1;
  }

  .resize-left {
    left: 0px;
  }
  .resize-right {
    right: 0px;
  }

  .toggle {
    cursor: pointer;
    outline: none;
    position: absolute;
    z-index: 2;
    top: calc(50% - 20px);
    height: 40px;
    width: 8px;
    padding: 0px;
    border: none;
    background-color: $primary;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    &:hover {
      background-color: #0790fc;
    }
    &:before {
      content: '';
      position: absolute;
      background: transparent url($ms-image-sprites) no-repeat;
      height: 20px;
      width: 8px;
      top: auto;
      left: 0;
    }
    &.expand {
      &:before {
        content: '';
        position: absolute;
        background: transparent url($ms-image-sprites) no-repeat;
        height: 20px;
        width: 8px;
        top: auto;
        left: 0;
      }
    }
  }

  .toggle-left {
    left: -2px;
    &:before {
      background-position: -320px -50px !important;
    }
    &.expand {
      &:before {
        background-position: -320px -74px !important;
      }
    }
  }

  .toggle-right {
    right: -2px;
    &:before {
      background-position: -320px -74px !important;
    }
    &.expand {
      &:before {
        background-position: -320px -50px !important;
      }
    }
  }
}
</style>
